import { DisableIntegrationAction } from '@modules/integrationsV2/components/DisableIntegrationAction';
import { PreviewFeedbackCardAction } from '@modules/integrationsV2/components/PreviewFeedbackCardAction';
import { ReauthorizeIntegrationAction } from '@modules/integrationsV2/components/ReauthorizeIntegrationAction';
import { useFetchConnectedIntegrations } from '@modules/integrationsV2/hooks';
import { IntegrationSource } from '@modules/integrationsV2/types';
import {
  getIsIntegrationChannelsMetadataSupported,
  trackIntegrationPageEvent,
} from '@modules/integrationsV2/utils';
import { MoreVertRounded } from '@mui/icons-material';
import { Box, IconButton, Menu } from '@mui/material';
import { IntegrationType } from 'generated/graphql';
import { ReactNode, useMemo, useState } from 'react';

interface IntegrationSourceMoreActionsMenuProps {
  integrationSource: IntegrationSource;
}

export const IntegrationSourceMoreActionsMenu = ({
  integrationSource,
}: IntegrationSourceMoreActionsMenuProps) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const { connectedIntegrations } = useFetchConnectedIntegrations({
    fetchPolicy: 'cache-first',
  });

  const {
    channel: integrationChannel,
    integrationID,
    integrationType,
    namespace,
    isSingleConnectionSource,
    isIngestionInProgress,
  } = integrationSource;

  const currentIntegration = isSingleConnectionSource
    ? connectedIntegrations.find(
        (integration) => integration.id === integrationID,
      )
    : null;

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
    trackIntegrationPageEvent({
      eventName: 'Integrations - Integration Actions Menu Opened',
      integration: {
        channelType: integrationChannel.type,
        integrationType,
        namespace,
      },
    });
  };
  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const isMenuOpen = Boolean(menuAnchor);

  const menuItems = useMemo(() => {
    const items: ReactNode[] = [];

    if (currentIntegration) {
      items.push(
        <DisableIntegrationAction
          channelType={currentIntegration.channel.type}
          integrationID={currentIntegration.id}
          integrationType={currentIntegration.integrationType}
          namespace={currentIntegration.namespace}
        />,
      );
    }

    const isIntegrationChannelsMetadataSupported =
      getIsIntegrationChannelsMetadataSupported(integrationSource.channel.type);

    if (currentIntegration || isIntegrationChannelsMetadataSupported) {
      items.push(
        <ReauthorizeIntegrationAction
          integrationChannel={integrationChannel}
          integrationID={integrationID}
          integrationType={integrationType}
        />,
      );
    }

    const isFeedbackIntegration = integrationType === IntegrationType.Feedback;

    const isEmptyFileUploadIntegration =
      currentIntegration?.data.fileUpload?.isEmpty;

    const showPreviewFeedbackCardButton = isSingleConnectionSource
      ? isFeedbackIntegration &&
        !isEmptyFileUploadIntegration &&
        !isIngestionInProgress
      : isFeedbackIntegration;

    const sourceDisplayName = `${integrationChannel.displayName} ${
      namespace ? ` - ${namespace}` : ''
    }`;

    if (showPreviewFeedbackCardButton) {
      items.push(
        <PreviewFeedbackCardAction
          integrationSource={integrationSource}
          sourceDisplayName={sourceDisplayName}
        />,
      );
    }

    return items;
  }, [
    currentIntegration,
    integrationChannel,
    integrationID,
    integrationSource,
    integrationType,
    isIngestionInProgress,
    isSingleConnectionSource,
    namespace,
  ]);

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton color="primary" onClick={openMenu}>
        <MoreVertRounded />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={isMenuOpen}
        onClose={closeMenu}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer - 1,
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: '150px',
            },
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menuItems.map((item, index) => (
          <Box key={index}>{item}</Box>
        ))}
      </Menu>
    </>
  );
};
